import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { studentLeavesService, StudentLeave } from '../../../services/studentLeavesService';
import { toast } from 'react-toastify';
import { FileText, Clock, CheckCircle, XCircle } from 'lucide-react';
import {
  TeacherStatsGrid,
  TeacherStatCard,
  TeacherFilterBar,
  TeacherSelect,
  TeacherPanel,
  TeacherLoading,
} from '../shared';

const StudentLeavesTab: React.FC = () => {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState<StudentLeave[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeave, setSelectedLeave] = useState<StudentLeave | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');

  useEffect(() => {
    fetchLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.schoolId, user?.classId]);

  const fetchLeaves = async () => {
    if (!user?.schoolId || !user?.classId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { leaves: data } = await studentLeavesService.getLeavesByClass(
        user.schoolId,
        user.classId
      );
      setLeaves(data);
    } catch (error: any) {
      console.error('Error fetching student leaves:', error);
      toast.error('Failed to load student leaves');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (leaveId: string) => {
    if (!user?.schoolId) return;

    try {
      setActionLoading(leaveId);
      await studentLeavesService.approveLeave(user.schoolId, leaveId);
      setLeaves((prev) =>
        prev.map((leave) =>
          leave.leaveId === leaveId ? { ...leave, status: 'approved' as const } : leave
        )
      );
      toast.success('Leave request approved successfully');
    } catch (error: any) {
      console.error('Error approving leave:', error);
      toast.error('Failed to approve leave');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (leaveId: string) => {
    if (!user?.schoolId) return;

    try {
      setActionLoading(leaveId);
      await studentLeavesService.rejectLeave(user.schoolId, leaveId);
      setLeaves((prev) =>
        prev.map((leave) =>
          leave.leaveId === leaveId ? { ...leave, status: 'rejected' as const } : leave
        )
      );
      toast.success('Leave request rejected');
    } catch (error: any) {
      console.error('Error rejecting leave:', error);
      toast.error('Failed to reject leave');
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewDetails = (leave: StudentLeave) => {
    setSelectedLeave(leave);
    setViewDialogOpen(true);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString || '-';
    }
  };

  const calculateDuration = (fromDate: string, toDate: string) => {
    const start = new Date(fromDate);
    const end = new Date(toDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const getStatusBadge = (status: string) => {
    const s = status.toLowerCase();
    if (s === 'approved') return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    if (s === 'rejected') return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
  };

  const stats = useMemo(() => ({
    total: leaves.length,
    pending: leaves.filter((l) => l.status.toLowerCase() === 'pending').length,
    approved: leaves.filter((l) => l.status.toLowerCase() === 'approved').length,
    rejected: leaves.filter((l) => l.status.toLowerCase() === 'rejected').length,
  }), [leaves]);

  const filteredLeaves = useMemo(() => {
    if (!filterStatus) return leaves;
    return leaves.filter(l => l.status.toLowerCase() === filterStatus);
  }, [leaves, filterStatus]);

  if (loading) {
    return <TeacherLoading message="Loading student leave requests..." />;
  }

  return (
    <div className="space-y-4">
      <TeacherStatsGrid>
        <TeacherStatCard title="Total Requests" value={stats.total} icon={FileText} color="indigo" />
        <TeacherStatCard title="Pending" value={stats.pending} icon={Clock} color="amber" />
        <TeacherStatCard title="Approved" value={stats.approved} icon={CheckCircle} color="emerald" />
        <TeacherStatCard title="Rejected" value={stats.rejected} icon={XCircle} color="rose" />
      </TeacherStatsGrid>

      <TeacherFilterBar>
        <TeacherSelect
          value={filterStatus}
          onChange={setFilterStatus}
          className="sm:w-48"
          options={[
            { value: '', label: 'All Status' },
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'rejected', label: 'Rejected' },
          ]}
        />
      </TeacherFilterBar>

      <TeacherPanel title="Student Leave Requests" noPadding>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Student</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Roll No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">From</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">To</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Attachment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredLeaves.map((leave) => (
                <tr
                  key={leave.leaveId}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  onClick={() => handleViewDetails(leave)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {leave.student?.profilePic ? (
                        <img
                          src={leave.student.profilePic}
                          alt={leave.student.name}
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-xs">
                          {(leave.student?.name || leave.studentId).charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white">
                        {leave.student?.name || leave.studentId}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    {leave.student?.rollNo || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 capitalize">
                    {leave.leaveType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    {formatDate(leave.fromDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    {formatDate(leave.toDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    {calculateDuration(leave.fromDate, leave.toDate)} day(s)
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusBadge(leave.status)}`}>
                      {leave.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                    {leave.fileUrl ? (
                      <a href={leave.fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        View
                      </a>
                    ) : <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                    {leave.status.toLowerCase() === 'pending' && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleApprove(leave.leaveId)}
                          disabled={actionLoading === leave.leaveId}
                          className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                          title="Approve"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleReject(leave.leaveId)}
                          disabled={actionLoading === leave.leaveId}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                          title="Reject"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredLeaves.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No student leave requests found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Student leave requests for your class will appear here.
            </p>
          </div>
        )}
      </TeacherPanel>

      {/* View Details Dialog */}
      {viewDialogOpen && selectedLeave && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Leave Request Details</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Student Info */}
              <div className="flex items-center gap-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                {selectedLeave.student?.profilePic ? (
                  <img
                    src={selectedLeave.student.profilePic}
                    alt={selectedLeave.student.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                    {(selectedLeave.student?.name || selectedLeave.studentId).charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {selectedLeave.student?.name || selectedLeave.studentId}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Roll No: {selectedLeave.student?.rollNo || '-'}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Leave Type</p>
                <p className="text-sm text-gray-900 dark:text-white capitalize">{selectedLeave.leaveType}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">From</p>
                  <p className="text-sm text-gray-900 dark:text-white">{formatDate(selectedLeave.fromDate)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">To</p>
                  <p className="text-sm text-gray-900 dark:text-white">{formatDate(selectedLeave.toDate)}</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Duration</p>
                <p className="text-sm text-gray-900 dark:text-white">{calculateDuration(selectedLeave.fromDate, selectedLeave.toDate)} day(s)</p>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Reason</p>
                <p className="text-sm text-gray-900 dark:text-white">{selectedLeave.reason}</p>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</p>
                <span className={`inline-flex mt-1 px-2 py-1 text-xs font-semibold rounded-full capitalize ${getStatusBadge(selectedLeave.status)}`}>
                  {selectedLeave.status}
                </span>
              </div>

              {selectedLeave.fileUrl && (
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Attachment</p>
                  <a
                    href={selectedLeave.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    View Attachment
                  </a>
                </div>
              )}

              {selectedLeave.status.toLowerCase() === 'pending' && (
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      handleApprove(selectedLeave.leaveId);
                      setViewDialogOpen(false);
                    }}
                    disabled={actionLoading === selectedLeave.leaveId}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Approve
                  </button>
                  <button
                    onClick={() => {
                      handleReject(selectedLeave.leaveId);
                      setViewDialogOpen(false);
                    }}
                    disabled={actionLoading === selectedLeave.leaveId}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Reject
                  </button>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setViewDialogOpen(false)}
                className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentLeavesTab;
